import React, { useState } from 'react';
import './App.css';
import { 
  LanguageSelector, 
  RecordingButton, 
  RecordingIndicator, 
  PermissionStatus,
  TranscriptionDisplay,
  ErrorDisplay,
  MicrophoneSelector
} from './components';
import './components/LanguageSelector.css';
import './components/RecordingButton.css';
import './components/RecordingIndicator.css';
import './components/PermissionStatus.css';
import './components/TranscriptionDisplay.css';
import './components/ErrorDisplay.css';
import './components/MicrophoneSelector.css';
import { LanguageOption, DEFAULT_LANGUAGE, AudioFormat } from './types';
import { TranscriptionResult } from './types/transcription';
import { useAudioCapture } from './hooks/useAudioCapture';
import { useWebSocketClient } from './hooks/useWebSocketClient';
import { useMicrophoneDevices } from './hooks/useMicrophoneDevices';
import { AudioProcessor } from './services/AudioProcessor';

function App() {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(DEFAULT_LANGUAGE);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [transcriptionResults, setTranscriptionResults] = useState<TranscriptionResult[]>([]);
  
  // WebSocket client hook
  const { state: wsState, actions: wsActions } = useWebSocketClient({
    onTranscription: (result) => {
      console.log('📝 [App] Received transcription:', result);
      setTranscriptionResults((prev: TranscriptionResult[]) => [...prev, result]);
    },
    onError: (error) => {
      console.error('❌ [App] WebSocket error:', error);
      setCurrentError(error.message);
    },
    onStatusUpdate: (status) => {
      console.log('📊 [App] Status update:', status);
    },
    onConnectionChange: (status) => {
      console.log('🔌 [App] Connection status changed to:', status);
    }
  });
  
  // Log WebSocket state changes
  React.useEffect(() => {
    console.log('🔍 [App] WebSocket state changed:', {
      connectionStatus: wsState.connectionStatus,
      isReady: wsState.isReady,
      error: wsState.error,
      sessionId: wsState.sessionId
    });
  }, [wsState.connectionStatus, wsState.isReady, wsState.error, wsState.sessionId]);

  // Microphone devices hook
  const { state: micState, actions: micActions } = useMicrophoneDevices();

  // Audio capture hook with WebSocket integration
  const audioChunkCountRef = React.useRef(0);
  const wsStateRef = React.useRef(wsState);
  const wsActionsRef = React.useRef(wsActions);
  
  // Keep refs updated
  React.useEffect(() => {
    wsStateRef.current = wsState;
    wsActionsRef.current = wsActions;
  }, [wsState, wsActions]);
  
  const { state: audioCaptureState, actions: audioCaptureActions } = useAudioCapture((chunk) => {
    audioChunkCountRef.current++;
    const chunkNum = audioChunkCountRef.current;
    
    // Use refs to get current state (not stale closure)
    const currentWsState = wsStateRef.current;
    const currentWsActions = wsActionsRef.current;
    
    // Log every chunk for debugging
    console.log(`🎵 [App] Audio chunk #${chunkNum} received, size: ${chunk.data.byteLength} bytes`);
    console.log(`🎵 [App] WebSocket ready: ${currentWsState.isReady}, connection status: ${currentWsState.connectionStatus}`);
    
    if (currentWsState.isReady) {
      console.log(`🎵 [App] Sending audio chunk #${chunkNum} to WebSocket...`);
      const success = currentWsActions.sendAudioChunk(chunk);
      if (!success) {
        console.error(`❌ [App] Failed to send audio chunk #${chunkNum} to WebSocket`);
      } else {
        console.log(`✅ [App] Audio chunk #${chunkNum} sent successfully`);
      }
    } else {
      console.warn(`⚠️ [App] WebSocket not ready (status: ${currentWsState.connectionStatus}), dropping audio chunk #${chunkNum}`);
    }
  });

  const handleLanguageChange = (language: LanguageOption) => {
    console.log('🌐 [App] Language changed to:', language);
    setSelectedLanguage(language);
    
    // Send language config to WebSocket if connected and ready
    if (wsState.isReady) {
      console.log('🌐 [App] Sending language config to WebSocket');
      wsActions.sendConfig(language);
    } else {
      console.log('🌐 [App] Language changed but WebSocket not ready - will send config when connected');
    }
  };

  // Track if we should connect to WebSocket
  const [shouldConnect, setShouldConnect] = React.useState(false);
  
  // Log initial render
  React.useEffect(() => {
    console.log('🎬 [App] Component mounted');
    console.log('🎬 [App] Initial state:', {
      shouldConnect,
      wsConnectionStatus: wsState.connectionStatus,
      hasPermission: micState.hasPermission,
      isCapturing: audioCaptureState.isCapturing
    });
  }, []);
  
  // Connect to WebSocket only when needed
  const connectionAttemptedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (shouldConnect && wsState.connectionStatus === 'disconnected' && !connectionAttemptedRef.current) {
      console.log('🔌 [App] Connecting to WebSocket for transcription...');
      // Use the current domain with secure WebSocket (wss://) for HTTPS sites
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/transcribe`;
      console.log('🔌 [App] WebSocket URL:', wsUrl);
      wsActions.connect(wsUrl);
      connectionAttemptedRef.current = true;
    }
    
    // Reset flag when shouldConnect becomes false
    if (!shouldConnect) {
      connectionAttemptedRef.current = false;
    }
  }, [shouldConnect, wsState.connectionStatus]); // Removed wsActions.connect from deps

  // Cleanup WebSocket on unmount only
  React.useEffect(() => {
    return () => {
      console.log('🔌 [App] Component unmounting, disconnecting WebSocket...');
      wsActions.disconnect();
    };
  }, []); // Empty deps - only disconnect on unmount

  const handleRecordingToggle = async () => {
    console.log('🎤 [App] Recording toggle clicked');
    console.log('🎤 [App] Current audio capture state:', audioCaptureState);
    
    try {
      setCurrentError(null); // Clear any existing errors
      
      if (audioCaptureState.isCapturing) {
        console.log('🛑 [App] Stopping recording...');
        
        // Stop transcription session
        if (wsState.isReady) {
          console.log('🛑 [App] Stopping transcription session');
          wsActions.sendMessage({
            type: 'stop_session',
            payload: {},
            messageId: typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36)
          });
        }
        
        // Stop recording
        audioCaptureActions.stopCapture();
        
        // Optionally disconnect WebSocket to save resources
        // (Comment out if you want to keep connection for faster restart)
        setTimeout(() => {
          if (!audioCaptureState.isCapturing) {
            console.log('🔌 [App] Disconnecting WebSocket after recording stopped');
            wsActions.disconnect();
            setShouldConnect(false);
          }
        }, 1000);
      } else {
        console.log('▶️ [App] Starting recording...');
        console.log('▶️ [App] Current state:', {
          hasPermission: micState.hasPermission,
          wsIsReady: wsState.isReady,
          isCapturing: audioCaptureState.isCapturing
        });
        
        // Start recording
        if (!micState.hasPermission) {
          console.log('🔐 [App] No microphone permission, requesting and enumerating devices...');
          // Request permission and enumerate devices
          const granted = await micActions.requestPermissionAndEnumerate();
          console.log('🔐 [App] Permission granted:', granted);
          
          if (!granted) {
            console.error('❌ [App] Microphone permission denied');
            setCurrentError('Microphone permission is required to start transcription. Please allow microphone access and try again.');
            return;
          }
          
          // Also update audio capture permission state
          console.log('🔐 [App] Updating audio capture permission state...');
          const audioCapturePermission = await audioCaptureActions.requestPermission();
          console.log('🔐 [App] Audio capture permission result:', audioCapturePermission);
          
          if (!audioCapturePermission) {
            console.error('❌ [App] Audio capture permission failed');
            setCurrentError('Failed to initialize audio capture. Please try again.');
            return;
          }
          
          // Wait a moment for state to update
          console.log('⏳ [App] Waiting for permission state to update...');
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          // Even if micState has permission, make sure audioCaptureState also has it
          if (!audioCaptureState.hasPermission) {
            console.log('🔐 [App] Microphone permission exists but audio capture needs permission...');
            const audioCapturePermission = await audioCaptureActions.requestPermission();
            console.log('🔐 [App] Audio capture permission result:', audioCapturePermission);
            
            if (!audioCapturePermission) {
              console.error('❌ [App] Audio capture permission failed');
              setCurrentError('Failed to initialize audio capture. Please try again.');
              return;
            }
            
            // Wait a moment for state to update
            console.log('⏳ [App] Waiting for permission state to update...');
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        console.log('✅ [App] Microphone permission confirmed');

        // Get browser's native audio format (includes actual sample rate)
        console.log('🎵 [App] Detecting browser audio capabilities...');
        const browserAudioFormat = await AudioProcessor.getBrowserPreferredFormat();
        console.log('🎵 [App] Browser audio format:', browserAudioFormat);
        
        // Use browser's native sample rate for best compatibility
        const audioFormat: AudioFormat = {
          sampleRate: browserAudioFormat.sampleRate,
          channels: 1,
          bitsPerSample: 16,
        };
        console.log('🎵 [App] Using audio format:', audioFormat);

        // Initiate WebSocket connection if not already connected
        console.log('🔍 [App] Checking WebSocket readiness:', wsState.isReady);
        if (!wsState.isReady) {
          console.log('🔌 [App] Initiating WebSocket connection...');
          console.log('🔌 [App] Setting shouldConnect to true');
          setShouldConnect(true);
          console.log('🔌 [App] shouldConnect has been set to true');
          
          // Wait for connection to establish with timeout
          console.log('⏳ [App] Waiting for connection to establish...');
          const maxWaitTime = 5000; // 5 seconds max
          const checkInterval = 100; // Check every 100ms
          let waited = 0;
          
          while (!wsActions.isHealthy() && waited < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            waited += checkInterval;
          }
          
          console.log(`⏳ [App] Wait complete after ${waited}ms, checking connection status...`);
          
          if (!wsActions.isHealthy()) {
            console.error('❌ [App] WebSocket connection not ready after timeout');
            setCurrentError('Failed to connect to transcription service. Please try again.');
            return;
          }
          
          console.log('✅ [App] WebSocket connection established and healthy');
        }

        // Verify connection is still healthy before proceeding
        console.log('🔍 [App] Final WebSocket health check before starting...');
        if (!wsActions.isHealthy()) {
          console.error('❌ [App] WebSocket connection lost before starting transcription');
          setCurrentError('Connection lost. Please try again.');
          return;
        }
        console.log('✅ [App] WebSocket is healthy, proceeding...');

        // Send language config and start session with actual sample rate
        console.log('🌐 [App] Sending language config to WebSocket');
        console.log(`🎵 [App] Configuring Transcribe with sample rate: ${audioFormat.sampleRate}Hz`);
        
        const configSuccess = wsActions.sendConfig(selectedLanguage, 'pcm', audioFormat.sampleRate);
        console.log('🌐 [App] Config sent, success:', configSuccess);
        
        if (!configSuccess) {
          console.error('❌ [App] Failed to send language config');
          setCurrentError('Failed to configure transcription service. Please try again.');
          return;
        }
        
        // Wait a moment for config to be processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('🎬 [App] Starting transcription session');
        const startSuccess = wsActions.sendMessage({
          type: 'start_session',
          payload: {},
          messageId: typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36)
        });
        console.log('🎬 [App] Start session message sent, success:', startSuccess);
        
        if (!startSuccess) {
          console.error('❌ [App] Failed to start transcription session');
          setCurrentError('Failed to start transcription session. Please try again.');
          return;
        }
        
        // Wait for transcription session to be ready
        console.log('⏳ [App] Waiting for transcription session to start...');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Final health check before starting audio capture
        if (!wsActions.isHealthy()) {
          console.error('❌ [App] WebSocket connection lost after starting session');
          setCurrentError('Connection lost after starting session. Please try again.');
          return;
        }

        console.log('🎵 [App] Starting audio capture with format:', audioFormat);
        console.log('🎯 [App] Using selected microphone:', micState.selectedDeviceId);
        console.log('🔍 [App] Final permission check - audioCaptureState.hasPermission:', audioCaptureState.hasPermission);
        
        // Force start since we've already confirmed permission through micState
        console.log('🚀 [App] Force starting audio capture (permission confirmed via micState)');
        await audioCaptureActions.startCapture({
          format: audioFormat,
          chunkSizeMs: 100,
        }, micState.selectedDeviceId || undefined, true); // forceStart = true
        console.log('✅ [App] Audio capture started successfully');
        
        // Verify WebSocket is still healthy after starting capture
        console.log('🔍 [App] Post-capture WebSocket health check...');
        if (!wsActions.isHealthy()) {
          console.error('❌ [App] WebSocket connection lost after starting audio capture');
          setCurrentError('Connection lost. Stopping recording...');
          audioCaptureActions.stopCapture();
          return;
        }
        console.log('✅ [App] All systems ready - recording in progress');
      }
    } catch (error) {
      console.error('❌ [App] Error toggling recording:', error);
      setCurrentError('Failed to start recording. Please check your microphone and try again.');
    }
  };

  const handleErrorDismiss = () => {
    setCurrentError(null);
  };

  const handleErrorRetry = () => {
    setCurrentError(null);
    // Retry the last action (in this case, try to start recording)
    handleRecordingToggle();
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Web Transcribe Streaming</h1>
        <p>Real-time audio transcription application</p>
      </header>
      <main className="App-main">
        {/* Error Display */}
        {(currentError || audioCaptureState.error) && (
          <ErrorDisplay
            error={currentError || audioCaptureState.error}
            onDismiss={handleErrorDismiss}
            onRetry={handleErrorRetry}
          />
        )}

        {/* Permission Status */}
        <PermissionStatus
          hasPermission={micState.hasPermission}
          isRequestingPermission={micState.isRequestingPermission}
          isSupported={audioCaptureState.isSupported}
          error={audioCaptureState.error}
          onRequestPermission={micActions.requestPermissionAndEnumerate}
        />

        {/* Microphone Selection */}
        {micState.hasPermission && (
          <MicrophoneSelector
            devices={micState.availableDevices}
            selectedDeviceId={micState.selectedDeviceId}
            onDeviceChange={micActions.selectDevice}
            disabled={audioCaptureState.isCapturing}
            isLoading={micState.isEnumeratingDevices}
          />
        )}

        {/* Language Selection */}
        <LanguageSelector
          selectedLanguage={selectedLanguage}
          onLanguageChange={handleLanguageChange}
          disabled={audioCaptureState.isCapturing}
        />
        <p className="selected-language-display">
          Selected: {selectedLanguage.name} ({selectedLanguage.transcribeCode})
        </p>

        {/* Recording Controls */}
        <div className="recording-controls">
          <RecordingIndicator isRecording={audioCaptureState.isCapturing} />
          <RecordingButton
            isRecording={audioCaptureState.isCapturing}
            disabled={!micState.hasPermission || !audioCaptureState.isSupported}
            onToggle={handleRecordingToggle}
          />
        </div>

        {/* Transcription Display */}
        <div className="transcription-section">
          <TranscriptionDisplay
            results={transcriptionResults}
            isActive={audioCaptureState.isCapturing}
          />
        </div>
      </main>
    </div>
  );
}

export default App;